"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AudioDevicesMgrService = void 0;
class AudioDevicesMgrService {
    constructor($rootScope, $window, mediaDevices, processVolume, $log, webRtcAudio, $translate) {
        this.$rootScope = $rootScope;
        this.$window = $window;
        this.mediaDevices = mediaDevices;
        this.processVolume = processVolume;
        this.$log = $log;
        this.webRtcAudio = webRtcAudio;
        this.$translate = $translate;
        this.audioDevices = [];
        this.microphoneDevices = [];
        this.ringingDevices = [];
        this.AUDIO_DEVICE_UPDATE_EVT = "AudioDevicesUpdateEvent";
        this.MIC_DEVICE_UPDATE_EVT = "MicrophoneDevicesUpdateEvent";
        this.RINGING_DEVICE_UPDATE_EVT = "RingingDevicesUpdateEvent";
        this.AUDIO_DEVICE_PICKED_EVT = "AudioDevicePickedEvent";
        this.MIC_DEVICE_PICKED_EVT = "MicrophoneDevicePickedEvent";
        this.RINGING_DEVICE_PICKED_EVT = "RingingDevicePickedEvent";
        this.testAudioElements = {
            output: null,
            ringing: null,
        };
        this.playingStates = {
            output: false,
            ringing: false,
        };
        this.microphoneTestStream = null;
        this.audioContext = null;
        this.analyser = null;
        this.animationFrameId = null;
        this.isMicrophoneTestActive = false;
        this.microphoneMonitorAudio = null;
        this.filterAndBroadcast = (devices, kind, eventName) => {
            const filtered = this.filterDevicesByKind(devices, kind);
            this.$rootScope.$broadcast(eventName, filtered);
            return filtered;
        };
    }
    changeAudioOutputDevice(deviceId) {
        this.$window.xc_webrtc.applyAudioOutputToElements(deviceId);
        this.$window.localStorage.setItem("AudioOutputDeviceId", deviceId);
        this.webRtcAudio.changeRingingDevice(deviceId, true);
        this.updateAudioTestSinkId("output", deviceId);
        this.updateMicrophoneTestSinkId(deviceId);
    }
    changeAudioInputDevice(deviceId) {
        this.$window.SIPml.setGlobalMicrophoneDeviceId(deviceId);
        this.$window.xc_webrtc
            .swapCurrentMicrophone(deviceId)
            .then((newStream) => {
            this.processVolume.onMicChange(newStream);
        })
            .catch((err) => {
            this.$log.debug(err);
        });
        this.$window.localStorage.setItem("AudioInputDeviceId", deviceId);
        if (this.isMicrophoneTestActive) {
            this.stopMicrophoneTest();
            this.startMicrophoneTest(deviceId);
        }
    }
    changeRingingDevice(deviceId) {
        this.$window.localStorage.setItem("SecondaryRingingDeviceId", deviceId);
        this.webRtcAudio.changeRingingDevice(deviceId);
        if (deviceId === "null") {
            this.stopAudioTest("ringing");
        }
        else {
            this.updateAudioTestSinkId("ringing", deviceId);
        }
    }
    updateAudioTestSinkId(deviceType, deviceId) {
        const testAudio = this.testAudioElements[deviceType];
        if (testAudio && typeof testAudio.setSinkId === "function") {
            testAudio
                .setSinkId(deviceId)
                .then(() => {
                this.$log.debug(`Test audio sink ID updated for ${deviceType}:`, deviceId);
            })
                .catch((err) => {
                this.$log.error(`Error updating test audio sink ID for ${deviceType}:`, err);
            });
        }
    }
    updateMicrophoneTestSinkId(deviceId) {
        if (this.microphoneMonitorAudio && typeof this.microphoneMonitorAudio.setSinkId === "function") {
            this.microphoneMonitorAudio
                .setSinkId(deviceId)
                .then(() => {
                this.$log.debug("Microphone monitor sink ID updated to:", deviceId);
            })
                .catch((err) => {
                this.$log.error("Error updating microphone monitor sink ID:", err);
            });
        }
    }
    initAudioDevices() {
        this.mediaDevices
            .getAudioDevices()
            .then(this.initPreviousAudioDevices.bind(this))
            .catch((err) => {
            this.$log.error("Error when getting devices", err);
        });
    }
    initPreviousAudioDevices(devices) {
        this.sortDevices(devices);
        this.initOutputs();
        this.initInputs();
        this.initRinging();
    }
    sortDevices(devices) {
        const noneDevice = {
            deviceId: "null",
            kind: "audiooutput",
            label: this.$translate.instant("SECONDARY_RINGING_NO_DEVICE_LABEL"),
        };
        this.audioDevices = this.filterAndBroadcast(devices, "audiooutput", this.AUDIO_DEVICE_UPDATE_EVT);
        this.ringingDevices = this.filterAndBroadcast([...devices, noneDevice].filter((device) => device.deviceId !== "default"), "audiooutput", this.RINGING_DEVICE_UPDATE_EVT);
        this.microphoneDevices = this.filterAndBroadcast(devices, "audioinput", this.MIC_DEVICE_UPDATE_EVT);
    }
    initRinging() {
        let selectedRingingDevice = this.selectDevice(this.ringingDevices, "SecondaryRingingDeviceId", "null");
        this.$rootScope.$broadcast(this.RINGING_DEVICE_PICKED_EVT, selectedRingingDevice);
    }
    initOutputs() {
        let selectedAudioDevice = this.selectDevice(this.audioDevices, "AudioOutputDeviceId");
        this.$rootScope.$broadcast(this.AUDIO_DEVICE_PICKED_EVT, selectedAudioDevice);
    }
    initInputs() {
        let selectedMicrophoneDevice = this.selectDevice(this.microphoneDevices, "AudioInputDeviceId");
        this.$rootScope.$broadcast(this.MIC_DEVICE_PICKED_EVT, selectedMicrophoneDevice);
    }
    selectDevice(devices, storageKey, defaultValue = "default") {
        const storedId = this.$window.localStorage.getItem(storageKey);
        return (devices.find((device) => device.deviceId == storedId) ||
            devices.find((device) => device.deviceId == defaultValue));
    }
    filterDevicesByKind(devices, kind) {
        return devices.filter((device) => device.kind === kind);
    }
    getUserRingtoneFile() {
        const userRingtone = this.$window.localStorage.getItem("user_ringtone");
        if (userRingtone) {
            try {
                const ringtone = JSON.parse(userRingtone);
                return ringtone.file;
            }
            catch (e) {
                this.$log.error("Error parsing user ringtone:", e);
            }
        }
        return "incoming_call.mp3";
    }
    cleanDeviceId(deviceId) {
        if (deviceId && typeof deviceId === "string" && deviceId.startsWith("string:")) {
            return deviceId.substring(7);
        }
        return deviceId;
    }
    isPlaying(deviceType) {
        return this.playingStates[deviceType] || false;
    }
    testAudioDevice(deviceId, deviceType) {
        deviceId = this.cleanDeviceId(deviceId);
        if (this.testAudioElements[deviceType]) {
            this.stopAudioTest(deviceType);
            return;
        }
        const audio = new Audio();
        audio.volume = 0.5;
        audio.loop = true;
        audio.src = "assets/audio/pratix/" + this.getUserRingtoneFile();
        this.testAudioElements[deviceType] = audio;
        const playAudio = () => {
            this.playingStates[deviceType] = true;
            this.$rootScope.$broadcast(`AudioTestStateChanged`, {
                deviceType,
                isPlaying: true,
            });
            audio
                .play()
                .then(() => {
                this.$log.debug(`Audio playing on ${deviceType}:`, deviceId);
            })
                .catch((err) => {
                this.$log.error(`Error playing audio on ${deviceType}:`, err);
                this.stopAudioTest(deviceType);
            });
        };
        if (deviceId && typeof audio.setSinkId === "function") {
            audio
                .setSinkId(deviceId)
                .then(() => {
                this.$log.debug(`Sink ID set for ${deviceType}:`, deviceId);
                playAudio();
            })
                .catch((err) => {
                this.$log.error(`Error setting sink ID for ${deviceType}:`, err);
                playAudio();
            });
        }
        else {
            playAudio();
        }
    }
    stopAudioTest(deviceType) {
        const audio = this.testAudioElements[deviceType];
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
            this.testAudioElements[deviceType] = null;
        }
        this.playingStates[deviceType] = false;
        this.$rootScope.$broadcast(`AudioTestStateChanged`, {
            deviceType,
            isPlaying: false,
        });
    }
    stopAllAudioTests() {
        this.stopAudioTest("output");
        this.stopAudioTest("ringing");
        this.stopMicrophoneTest();
    }
    isMicrophoneTesting() {
        return this.isMicrophoneTestActive;
    }
    toggleMicrophoneTest(deviceId) {
        if (this.isMicrophoneTestActive) {
            this.stopMicrophoneTest();
        }
        else {
            this.startMicrophoneTest(deviceId);
        }
    }
    startMicrophoneTest(deviceId) {
        const constraints = {
            audio: deviceId
                ? {
                    deviceId: { exact: deviceId },
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                }
                : {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                }
        };
        navigator.mediaDevices.getUserMedia(constraints)
            .then((stream) => {
            this.microphoneTestStream = stream;
            this.isMicrophoneTestActive = true;
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            this.audioContext = new AudioContextClass();
            this.analyser = this.audioContext.createAnalyser();
            const source = this.audioContext.createMediaStreamSource(stream);
            source.connect(this.analyser);
            this.analyser.fftSize = 256;
            const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
            const updateLevel = () => {
                if (!this.isMicrophoneTestActive || !this.analyser)
                    return;
                this.analyser.getByteFrequencyData(dataArray);
                const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                const level = Math.min(100, (average / 128) * 100);
                this.$rootScope.$broadcast('MicrophoneLevelUpdate', { level });
                this.animationFrameId = requestAnimationFrame(updateLevel);
            };
            updateLevel();
            this.microphoneMonitorAudio = document.createElement('audio');
            this.microphoneMonitorAudio.muted = false;
            this.microphoneMonitorAudio.volume = 1.0;
            this.microphoneMonitorAudio.srcObject = stream;
            const outputDeviceId = this.$window.localStorage.getItem("AudioOutputDeviceId");
            const cleanedDeviceId = outputDeviceId ? this.cleanDeviceId(outputDeviceId) : "";
            const playMonitor = () => {
                this.microphoneMonitorAudio.play()
                    .catch((err) => {
                    this.$log.error("Error playing microphone monitor:", err);
                });
            };
            if (cleanedDeviceId && typeof this.microphoneMonitorAudio.setSinkId === "function") {
                this.microphoneMonitorAudio
                    .setSinkId(cleanedDeviceId)
                    .then(() => {
                    this.$log.debug("Microphone monitor sink ID set to:", cleanedDeviceId);
                    playMonitor();
                })
                    .catch((err) => {
                    this.$log.error("Error setting microphone monitor sink ID:", err);
                    playMonitor();
                });
            }
            else {
                playMonitor();
            }
            this.$rootScope.$broadcast('MicrophoneTestStateChanged', { isActive: true });
        })
            .catch((err) => {
            this.$log.error('Error accessing microphone:', err);
            this.isMicrophoneTestActive = false;
            this.$rootScope.$broadcast('MicrophoneTestStateChanged', { isActive: false });
        });
    }
    stopMicrophoneTest() {
        this.isMicrophoneTestActive = false;
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        if (this.microphoneMonitorAudio) {
            this.microphoneMonitorAudio.pause();
            this.microphoneMonitorAudio.srcObject = null;
            this.microphoneMonitorAudio = null;
        }
        if (this.microphoneTestStream) {
            this.microphoneTestStream.getTracks().forEach((track) => track.stop());
            this.microphoneTestStream = null;
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        this.analyser = null;
        this.$rootScope.$broadcast('MicrophoneTestStateChanged', { isActive: false });
        this.$rootScope.$broadcast('MicrophoneLevelUpdate', { level: 0 });
    }
}
exports.AudioDevicesMgrService = AudioDevicesMgrService;
AudioDevicesMgrService.$inject = [
    "$rootScope",
    "$window",
    "mediaDevices",
    "processVolume",
    "$log",
    "webRtcAudio",
    "$translate",
];
